import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LoadingButton from '@mui/lab/LoadingButton';
import {
    Box,
    Button,
    Card,
    Checkbox,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    MenuItem,
    Select,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import moment from 'moment';
import { useDialog } from 'muibox';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { OnlyFansMetrics } from '../../../../hooks/useSextforceMetricsCampaignsOverview';
import useSextforceMetricsPartners from '../../../../hooks/useSextforceMetricsPartners';
import useSextforceMetricsWebhook, {
    createEmptyOnlyFansMetricsShareOptions,
    OnlyFansMetricsShareOptions,
    OnlyFansMetricsWebHook,
} from '../../../../hooks/useSextforceMetricsWebhook';
import { OnlyFansSubscriber } from '../../../../hooks/useSubscriber';
import { isValidUrl } from '../../../../utils/common';
import CopyToClipboardButton from '../../../common/CopyToClipboardButton';
import MoreInfoDialog from '../../../common/MoreInfoDialog';
import SyntaxHighlightedContent from '../../../common/SyntaxHighlightedContent';

const SextforceMetricsWebhookOptionsForm = ({
    metricData,
    initialValues,
    onChange,
}: {
    metricData: OnlyFansMetrics;
    initialValues: OnlyFansMetricsShareOptions;
    onChange: (values: OnlyFansMetricsShareOptions) => void;
}) => {
    const [selectedOption, setSelectedOption] = useState<OnlyFansMetricsShareOptions>(initialValues);

    const handleOptionChange = (option: keyof OnlyFansMetricsShareOptions, value: boolean) => {
        const newValues = { ...selectedOption, [option]: value };
        setSelectedOption(newValues);
        onChange(newValues);
    };

    return (
        <Box>
            <Typography component={'span'} variant="h6" sx={{ display: 'block', mt: 1 }}>
                Webhook Options
            </Typography>
            <Typography component={'span'} variant="body2" sx={{ display: 'block', mb: 1 }}>
                Select the information you want sent to the partner.
            </Typography>
            <Card sx={{ pl: 2, pr: 2, pt: 1, pb: 1 }}>
                <Grid
                    container
                    spacing={0}
                    flexGrow={1}
                    alignItems={'center'}
                    sx={{
                        '& .MuiFormControl-root': {
                            marginTop: 0,
                            marginBottom: 0,
                        },
                        '& .MuiFormControlLabel-root': {
                            marginRight: 1,
                        },
                    }}
                >
                    {metricData.type === 'trialLinkTrial' && (
                        <>
                            <Grid item xs={12}>
                                <FormControl>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={!!selectedOption.claimsCount}
                                                onChange={e => handleOptionChange('claimsCount', e.target.checked)}
                                            />
                                        }
                                        label="Claims Count"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={!!selectedOption.subscribeCounts}
                                                onChange={e => handleOptionChange('subscribeCounts', e.target.checked)}
                                            />
                                        }
                                        label="Subscribers Counts"
                                    />
                                </FormControl>
                            </Grid>
                        </>
                    )}

                    {metricData.type === 'promoCampaign' && (
                        <>
                            <Grid item xs={12}>
                                <FormControl>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={!!selectedOption.countSubscribers}
                                                onChange={e => handleOptionChange('countSubscribers', e.target.checked)}
                                            />
                                        }
                                        label="Subscribers Count"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={!!selectedOption.countTransitions}
                                                onChange={e => handleOptionChange('countTransitions', e.target.checked)}
                                            />
                                        }
                                        label="Clicks Count"
                                    />
                                </FormControl>
                            </Grid>
                        </>
                    )}

                    <Grid item xs={12}>
                        <FormControl>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={!!selectedOption.spendersCount}
                                        onChange={e => handleOptionChange('spendersCount', e.target.checked)}
                                    />
                                }
                                label="Spenders Count"
                            />
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <Typography component={'span'} variant="body1" fontWeight={'bold'} sx={{ display: 'block', mt: 1 }}>
                            Earnings
                        </Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <Grid container spacing={0.5} alignItems={'center'} justifyItems={'space-between'}>
                            <Grid item xs>
                                <Typography variant="body1" component={'div'}>
                                    Total Earnings
                                </Typography>
                            </Grid>
                            <Grid item xs="auto">
                                <Stack direction="row" spacing={0.5} alignItems={'center'}>
                                    <FormControl>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={!!selectedOption.totalEarnings}
                                                    onChange={e => handleOptionChange('totalEarnings', e.target.checked)}
                                                />
                                            }
                                            label="Gross"
                                        />
                                    </FormControl>
                                    <FormControl>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={!!selectedOption.totalEarningsNet}
                                                    onChange={e => handleOptionChange('totalEarningsNet', e.target.checked)}
                                                />
                                            }
                                            label="Net"
                                        />
                                    </FormControl>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12}>
                        <Grid container spacing={0.5} alignItems={'center'} justifyItems={'space-between'}>
                            <Grid item xs>
                                <Typography variant="body1" component={'div'}>
                                    Total Chargebacks
                                </Typography>
                            </Grid>
                            <Grid item xs="auto">
                                <Stack direction="row" spacing={0.5} alignItems={'center'}>
                                    <FormControl>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={!!selectedOption.totalChargebacks}
                                                    onChange={e => handleOptionChange('totalChargebacks', e.target.checked)}
                                                />
                                            }
                                            label="Gross"
                                        />
                                    </FormControl>
                                    <FormControl>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={!!selectedOption.totalChargebacksNet}
                                                    onChange={e => handleOptionChange('totalChargebacksNet', e.target.checked)}
                                                />
                                            }
                                            label="Net"
                                        />
                                    </FormControl>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12}>
                        <Grid container spacing={0.5} alignItems={'center'} justifyItems={'space-between'}>
                            <Grid item xs>
                                <Typography variant="body1" component={'div'}>
                                    Subscriptions
                                </Typography>
                            </Grid>
                            <Grid item xs="auto">
                                <Stack direction="row" spacing={0.5} alignItems={'center'}>
                                    <FormControl>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={!!selectedOption.totalSubscription}
                                                    onChange={e => handleOptionChange('totalSubscription', e.target.checked)}
                                                />
                                            }
                                            label="Gross"
                                        />
                                    </FormControl>
                                    <FormControl>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={!!selectedOption.totalSubscriptionNet}
                                                    onChange={e => handleOptionChange('totalSubscriptionNet', e.target.checked)}
                                                />
                                            }
                                            label="Net"
                                        />
                                    </FormControl>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12}>
                        <Grid container spacing={0.5} alignItems={'center'} justifyItems={'space-between'}>
                            <Grid item xs>
                                <Typography variant="body1" component={'div'}>
                                    Resubscriptions
                                </Typography>
                            </Grid>
                            <Grid item xs="auto">
                                <Stack direction="row" spacing={0.5} alignItems={'center'}>
                                    <FormControl>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={!!selectedOption.totalResubscription}
                                                    onChange={e => handleOptionChange('totalResubscription', e.target.checked)}
                                                />
                                            }
                                            label="Gross"
                                        />
                                    </FormControl>
                                    <FormControl>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={!!selectedOption.totalResubscriptionNet}
                                                    onChange={e => handleOptionChange('totalResubscriptionNet', e.target.checked)}
                                                />
                                            }
                                            label="Net"
                                        />
                                    </FormControl>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12}>
                        <Grid container spacing={0.5} alignItems={'center'} justifyItems={'space-between'}>
                            <Grid item xs>
                                <Typography variant="body1" component={'div'}>
                                    Messages
                                </Typography>
                            </Grid>
                            <Grid item xs="auto">
                                <Stack direction="row" spacing={0.5} alignItems={'center'}>
                                    <FormControl>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={!!selectedOption.totalMessage}
                                                    onChange={e => handleOptionChange('totalMessage', e.target.checked)}
                                                />
                                            }
                                            label="Gross"
                                        />
                                    </FormControl>
                                    <FormControl>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={!!selectedOption.totalMessageNet}
                                                    onChange={e => handleOptionChange('totalMessageNet', e.target.checked)}
                                                />
                                            }
                                            label="Net"
                                        />
                                    </FormControl>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12}>
                        <Grid container spacing={0.5} alignItems={'center'} justifyItems={'space-between'}>
                            <Grid item xs>
                                <Typography variant="body1" component={'div'}>
                                    Tips
                                </Typography>
                            </Grid>
                            <Grid item xs="auto">
                                <Stack direction="row" spacing={0.5} alignItems={'center'}>
                                    <FormControl>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={!!selectedOption.totalTip}
                                                    onChange={e => handleOptionChange('totalTip', e.target.checked)}
                                                />
                                            }
                                            label="Gross"
                                        />
                                    </FormControl>
                                    <FormControl>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={!!selectedOption.totalTipNet}
                                                    onChange={e => handleOptionChange('totalTipNet', e.target.checked)}
                                                />
                                            }
                                            label="Net"
                                        />
                                    </FormControl>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12}>
                        <Grid container spacing={0.5} alignItems={'center'} justifyItems={'space-between'}>
                            <Grid item xs>
                                <Typography variant="body1" component={'div'}>
                                    Posts
                                </Typography>
                            </Grid>
                            <Grid item xs="auto">
                                <Stack direction="row" spacing={0.5} alignItems={'center'}>
                                    <FormControl>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={!!selectedOption.totalPost}
                                                    onChange={e => handleOptionChange('totalPost', e.target.checked)}
                                                />
                                            }
                                            label="Gross"
                                        />
                                    </FormControl>
                                    <FormControl>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={!!selectedOption.totalPostNet}
                                                    onChange={e => handleOptionChange('totalPostNet', e.target.checked)}
                                                />
                                            }
                                            label="Net"
                                        />
                                    </FormControl>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12}>
                        <Grid container spacing={0.5} alignItems={'center'} justifyItems={'space-between'}>
                            <Grid item xs>
                                <Typography variant="body1" component={'div'}>
                                    Streams
                                </Typography>
                            </Grid>
                            <Grid item xs="auto">
                                <Stack direction="row" spacing={0.5} alignItems={'center'}>
                                    <FormControl>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={!!selectedOption.totalStream}
                                                    onChange={e => handleOptionChange('totalStream', e.target.checked)}
                                                />
                                            }
                                            label="Gross"
                                        />
                                    </FormControl>
                                    <FormControl>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={!!selectedOption.totalStreamNet}
                                                    onChange={e => handleOptionChange('totalStreamNet', e.target.checked)}
                                                />
                                            }
                                            label="Net"
                                        />
                                    </FormControl>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Card>
        </Box>
    );
};

const SextforceMetricsWebhookCustomWebhookForm = ({
    initialValues,
    onChange,
}: {
    initialValues: {
        url: string;
        name: string;
        description?: string;
        headers?: { [key: string]: string };
        parameters?: { [key: string]: string };
    };
    onChange: (values: {
        url: string;
        name: string;
        description?: string;
        headers?: { [key: string]: string };
        parameters?: { [key: string]: string };
    }) => void;
}) => {
    const [customWebhook, setCustomWebhook] = useState(initialValues);

    const handleInputChange = (key: keyof typeof customWebhook, value: any) => {
        const newValues = { ...customWebhook, [key]: value };
        setCustomWebhook(newValues);
        onChange(newValues);
    };

    return (
        <Box>
            <Typography component={'span'} variant="h6" sx={{ display: 'block', mt: 1 }}>
                <Stack direction="row" spacing={0.5} alignItems={'center'} justifyContent={'space-between'}>
                    <span>Custom Webhook</span>
                    <MoreInfoDialog title="Webhook Data Structure">
                        <Stack direction="column" spacing={1} alignItems={'flex-start'}>
                            <Typography variant="body1" component={'span'}>
                                Every time the promo link is updated, a POST request will be sent to the webhook URL. The request will
                                contain the subscriber's information, the metric information, and the counters.
                            </Typography>
                            <Typography variant="body1" component={'span'}>
                                The data will be sent as a JSON object in the body of the request. Depending on which options were selected,
                                the following fields are available:
                            </Typography>
                            <SyntaxHighlightedContent
                                language="javascript"
                                content={`{
    // Subscriber Info
    username: string;
    avatar?: string;
    // Metric Info
    metricId: Types.ObjectId;
    name: string | null;
    foreignId?: number;
    code?: number;
    type: 'tracking' | 'trial';
    createdAt?: Date;
    updatedAt?: Date;
    promoUrl: string;
    // Partner ID
    agencyPartnerMetricId?: string | number;
    // Counters
    clicksCount?: number;
    claimsCount?: number;
    // Earnings
    spendersCount?: number;
    totalEarnings?: number;
    totalEarningsNet?: number;
    totalChargebacks?: number;
    totalChargebacksNet?: number;
    totalSubscription?: number;
    totalSubscriptionNet?: number;
    totalResubscription?: number;
    totalResubscriptionNet?: number;
    totalMessage?: number;
    totalMessageNet?: number;
    totalTip?: number;
    totalTipNet?: number;
    totalPost?: number;
    totalPostNet?: number;
    totalStream?: number;
    totalStreamNet?: number;
}`}
                            />
                            <Typography variant="body1" component={'span'}>
                                The webhook must return a 200 OK response to acknowledge the receipt of the data, and return your local ID
                                for this tracking/trial link like this:
                            </Typography>
                            <SyntaxHighlightedContent
                                language="javascript"
                                content={`{
    linkId: string | number;
}`}
                            />
                            <Typography variant="body1" component={'span'}>
                                If the webhook fails to respond, this webhook will be deactivated and you will need to set it up again. If
                                you need to test your webhook, you can use a tool like Postman or a webhook testing service to simulate the
                                requests.
                            </Typography>
                        </Stack>
                    </MoreInfoDialog>{' '}
                </Stack>
            </Typography>
            <Typography component={'span'} variant="body2" sx={{ display: 'block', mb: 1 }}>
                Enter the webhook name and URL.
            </Typography>
            <Card sx={{ pl: 2, pr: 2, pt: 1, pb: 1 }}>
                <Grid container spacing={0.5} flexGrow={1} alignItems={'center'}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Name"
                            value={customWebhook.name}
                            onChange={e => handleInputChange('name', e.target.value)}
                            error={!customWebhook.name.trim()}
                            helperText={!customWebhook.name ? 'Name is required' : ''}
                            required
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Webhook URL"
                            value={customWebhook.url}
                            onChange={e => handleInputChange('url', e.target.value)}
                            error={isValidUrl(customWebhook.url) || customWebhook.url.trim() === ''}
                            helperText={isValidUrl(customWebhook.url) || customWebhook.url.trim() === '' ? 'Webhook URL is required' : ''}
                            required
                        />
                    </Grid>
                    {/* <Grid item xs={12}>
                        <Typography component={'span'} variant="body2" sx={{ display: 'block' }}>
                            Headers1
                        </Typography>
                        <SextforceMetricsWebhookCustomWebhookKeyValueForm
                            initialValues={initialValues.headers}
                            onChange={values => {
                                handleInputChange('headers', { ...customWebhook.headers, [values.key]: values.value });
                            }}
                            disallowedKeys={['Content-Type']}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography component={'span'} variant="body2" sx={{ display: 'block' }}>
                            Parameters
                        </Typography>
                        <SextforceMetricsWebhookCustomWebhookKeyValueForm
                            initialValues={initialValues.parameters}
                            onChange={values => {
                                handleInputChange('parameters', { ...customWebhook.parameters, [values.key]: values.value });
                            }}
                        />
                    </Grid> */}
                </Grid>
            </Card>
        </Box>
    );
};

const SextforceMetricsWebhookTableRow = ({
    metricData,
    webhook,
    isUpdatingWebhook,
    isDeletingWebhook,
    handleUpdateWebhook,
    handleDeleteWebhook,
}: {
    metricData: OnlyFansMetrics;
    webhook: OnlyFansMetricsWebHook;
    isUpdatingWebhook: boolean;
    isDeletingWebhook: boolean;
    handleUpdateWebhook: (webhook: OnlyFansMetricsWebHook) => void;
    handleDeleteWebhook: (webhookId: string | undefined) => void;
}) => {
    const [updatedWebhook, setUpdatedWebhook] = useState<OnlyFansMetricsWebHook>(webhook);
    const [isEditing, setIsEditing] = useState(false);

    return (
        <>
            <TableRow key={webhook._id}>
                <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems={'center'}>
                        <CopyToClipboardButton text={webhook._id!} />
                        <Typography variant="body2" component={'span'}>
                            {webhook.name}
                        </Typography>
                    </Stack>
                </TableCell>
                <TableCell>
                    <Typography variant="body2" component={'span'}>
                        {moment(webhook.createdAt).format('L')}
                    </Typography>
                </TableCell>
                <TableCell>
                    <Typography variant="body2" component={'span'}>
                        {moment(webhook.lastUpdateDate).format('L')}
                    </Typography>
                </TableCell>
                <TableCell align="center">
                    <Stack direction="row" spacing={0.5} alignItems={'center'}>
                        <IconButton
                            size="small"
                            color="primary"
                            disabled={isUpdatingWebhook}
                            onClick={() => {
                                setIsEditing(true);
                            }}
                        >
                            <EditIcon />
                        </IconButton>
                        <IconButton
                            size="small"
                            color="error"
                            disabled={isDeletingWebhook}
                            onClick={() => {
                                handleDeleteWebhook(webhook._id);
                            }}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Stack>
                </TableCell>
            </TableRow>
            {isEditing && (
                <TableRow>
                    <TableCell colSpan={4}>
                        <SextforceMetricsWebhookOptionsForm
                            metricData={metricData}
                            initialValues={updatedWebhook.options}
                            onChange={values => {
                                setUpdatedWebhook({ ...updatedWebhook, options: values });
                            }}
                        />
                        <Stack direction="column" spacing={0.5} alignItems={'center'} sx={{ mt: 1 }}>
                            <LoadingButton
                                variant="contained"
                                color="primary"
                                size="small"
                                loading={isUpdatingWebhook}
                                fullWidth
                                onClick={() => {
                                    handleUpdateWebhook({ ...updatedWebhook, options: updatedWebhook.options });
                                    setIsEditing(false);
                                }}
                            >
                                <span>Save</span>
                            </LoadingButton>
                            <Button
                                variant="outlined"
                                color="secondary"
                                size="small"
                                fullWidth
                                onClick={() => {
                                    setIsEditing(false);
                                }}
                            >
                                Cancel
                            </Button>
                        </Stack>
                    </TableCell>
                </TableRow>
            )}
        </>
    );
};

type Props = {
    subscriber: OnlyFansSubscriber;
    metricData: OnlyFansMetrics;
};

const SextforceMetricsShareDialogWebhook = (props: Props) => {
    const { subscriber, metricData } = props;
    const { enqueueSnackbar } = useSnackbar();
    const dialog = useDialog();

    const { data: partners, isLoading: isPartnersLoading } = useSextforceMetricsPartners();
    const [selectedPartnerId, setSelectedPartnerId] = useState<string | 'custom'>('');

    const [webhookOptions, setWebhookOptions] = useState<OnlyFansMetricsShareOptions>(createEmptyOnlyFansMetricsShareOptions());
    const [customWebhookDetails, setCustomWebhookDetails] = useState<{
        url: string;
        name: string;
        description?: string;
        headers?: { [key: string]: string };
        parameters?: { [key: string]: string };
    }>({ url: '', name: '' });

    // Check if there are any options selected
    const hasSelectedOptions = Object.keys(webhookOptions).some(key => webhookOptions[key as keyof OnlyFansMetricsShareOptions]);

    const {
        data: webhooks,
        isLoading: isWebhooksLoading,
        refetch: refetchWebhooks,
        createWebhook,
        isCreatingNewWebhook,
        updateWebhook,
        isUpdatingWebhook,
        deleteWebhook,
        isDeletingWebhook,
    } = useSextforceMetricsWebhook(metricData._id, metricData.type);

    const isLoading = isPartnersLoading || isWebhooksLoading || isCreatingNewWebhook || isUpdatingWebhook || isDeletingWebhook;

    const handleCreateNewWebhook = async () => {
        if (!selectedPartnerId) {
            return;
        }

        dialog
            .confirm({
                title: 'Create Webhook',
                message: 'Are you sure you want to create this webhook? The data will be shared with the selected partner.',
                ok: {
                    text: 'Create',
                    color: 'primary',
                    variant: 'contained',
                },
                cancel: {
                    text: 'Cancel',
                    color: 'secondary',
                    variant: 'outlined',
                },
            })
            .then(async () => {
                const webhook: OnlyFansMetricsWebHook = {
                    metricId: metricData._id,
                    type: 'metric',
                    name: partners?.find(partner => partner._id === selectedPartnerId)?.name || '',
                    ...(selectedPartnerId !== 'custom' && { agencyPartnerId: selectedPartnerId }),
                    ...(selectedPartnerId === 'custom' && {
                        name: customWebhookDetails.name,
                        url: customWebhookDetails.url,
                        description: customWebhookDetails.description,
                        headers: customWebhookDetails.headers,
                        parameters: customWebhookDetails.parameters,
                    }),
                    options: webhookOptions,
                    active: true,
                };

                await createWebhook(webhook)
                    .then(newWebhook => {
                        if (!newWebhook) {
                            enqueueSnackbar('Error creating webhook', { variant: 'error' });

                            return;
                        }

                        enqueueSnackbar(`Webhook created successfully`, { variant: 'success' });

                        setSelectedPartnerId('');
                        setWebhookOptions(createEmptyOnlyFansMetricsShareOptions());
                        refetchWebhooks();
                    })
                    .catch(error => {
                        enqueueSnackbar(`Error creating webhook: ${error.message}`, { variant: 'error' });
                    });
            })
            .catch(() => {
                // User cancelled the dialog
            });
    };

    const handleUpdateWebhook = async (webhook: OnlyFansMetricsWebHook) => {
        if (!webhook._id) {
            return;
        }

        const data = await updateWebhook(webhook)
            .then(updatedWebhook => {
                if (!updatedWebhook) {
                    enqueueSnackbar('Error updating webhook', { variant: 'error' });

                    return null;
                }

                enqueueSnackbar(`Webhook updated successfully`, { variant: 'success' });

                refetchWebhooks();

                return updatedWebhook;
            })
            .catch(error => {
                enqueueSnackbar(`Error updating webhook: ${error.message}`, { variant: 'error' });

                return null;
            });

        return data;
    };

    const handleDeleteWebhook = async (webhookId: string | undefined) => {
        if (!webhookId) {
            return;
        }

        dialog
            .confirm({
                title: 'Delete Webhook',
                message: 'Are you sure you want to delete this webhook?',
                ok: {
                    text: 'Delete',
                    color: 'error',
                    variant: 'contained',
                },
                cancel: {
                    text: 'Cancel',
                    color: 'secondary',
                    variant: 'outlined',
                },
            })
            .then(async () => {
                await deleteWebhook(webhookId)
                    .then(result => {
                        if (result) {
                            enqueueSnackbar(`Webhook deleted successfully`, { variant: 'success' });

                            refetchWebhooks();
                        } else {
                            enqueueSnackbar('Error deleting webhook', { variant: 'error' });
                        }
                    })
                    .catch(error => {
                        enqueueSnackbar(`Error deleting webhook: ${error.message}`, { variant: 'error' });
                    });
            })
            .catch(() => {
                // User cancelled the dialog
            });
    };

    return (
        <>
            <Grid container spacing={0.5} flexGrow={1} alignItems={'center'}>
                <Grid item xs={12}>
                    <Typography component={'span'} variant="h6" sx={{ display: 'block' }}>
                        Add Webhook
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <Typography component={'span'} variant="body2" sx={{ display: 'block' }}>
                        Select the partner you want to share your data with. If the partner is not listed, you can add a custom webhook URL.
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <Stack direction="row" spacing={0.5} alignItems={'center'}>
                        <FormControl fullWidth>
                            <Select
                                fullWidth
                                displayEmpty
                                value={selectedPartnerId}
                                onChange={e => {
                                    setSelectedPartnerId(e.target.value);
                                }}
                                disabled={isLoading}
                            >
                                <MenuItem value={''} disabled>
                                    Select a partner
                                </MenuItem>
                                {partners &&
                                    partners.map(partner => (
                                        <MenuItem key={partner._id} value={partner._id}>
                                            {partner.name}
                                        </MenuItem>
                                    ))}
                                <MenuItem value={'custom'}>Custom Webhook</MenuItem>
                            </Select>
                        </FormControl>
                        <Tooltip title="Open partner website in new tab" placement="top">
                            <span>
                                <IconButton
                                    size="large"
                                    color="primary"
                                    component={Link}
                                    to={
                                        partners?.find(partner => partner._id === selectedPartnerId)?.website
                                            ? `https://${partners?.find(partner => partner._id === selectedPartnerId)?.website || ''}`
                                            : ''
                                    }
                                    target="_blank"
                                    disabled={
                                        !selectedPartnerId ||
                                        isPartnersLoading ||
                                        !partners?.find(partner => partner._id === selectedPartnerId)?.url
                                    }
                                >
                                    <OpenInNewIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                </Grid>
                {selectedPartnerId === 'custom' && (
                    <Grid item xs={12}>
                        <SextforceMetricsWebhookCustomWebhookForm
                            initialValues={customWebhookDetails}
                            onChange={values => {
                                setCustomWebhookDetails(values);
                            }}
                        />
                    </Grid>
                )}
                {selectedPartnerId && (
                    <>
                        <Grid item xs={12}>
                            <Stack direction="column" spacing={0.5} alignItems={'flex-start'}>
                                <SextforceMetricsWebhookOptionsForm
                                    metricData={metricData}
                                    initialValues={webhookOptions}
                                    onChange={values => {
                                        setWebhookOptions(values);
                                    }}
                                />
                            </Stack>
                        </Grid>

                        <Grid item xs={12}>
                            <Stack direction="column" spacing={1} alignItems={'center'} sx={{ mt: 1, mb: 1 }}>
                                <LoadingButton
                                    variant="contained"
                                    color="primary"
                                    size="medium"
                                    fullWidth
                                    loading={isCreatingNewWebhook}
                                    disabled={!hasSelectedOptions}
                                    onClick={handleCreateNewWebhook}
                                >
                                    <span>Create Webhook</span>
                                </LoadingButton>

                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    size="medium"
                                    fullWidth
                                    onClick={() => {
                                        setSelectedPartnerId('');
                                        setWebhookOptions(createEmptyOnlyFansMetricsShareOptions());
                                    }}
                                >
                                    Cancel
                                </Button>
                            </Stack>
                        </Grid>
                    </>
                )}

                <Grid item xs={12}>
                    <Typography component={'span'} variant="h6" sx={{ display: 'block', mt: 2 }}>
                        Manage Webhooks
                    </Typography>
                </Grid>
                {isWebhooksLoading ? (
                    <Grid item xs={12}>
                        <Skeleton variant="text" width={'100%'} height={100} sx={{ mt: 2, mb: 2 }} />
                    </Grid>
                ) : (
                    <>
                        {webhooks && webhooks.length > 0 ? (
                            <Grid
                                item
                                xs={12}
                                sx={{
                                    display: 'block',
                                    overflowX: 'auto',
                                }}
                            >
                                <Table
                                    size="small"
                                    sx={{
                                        // No padding on the left and right of the table
                                        '& td': {
                                            '&:first-child': {
                                                paddingLeft: '0px',
                                            },
                                            '&:last-child': {
                                                paddingRight: '0px',
                                            },
                                        },
                                        '& th': {
                                            '&:first-child': {
                                                paddingLeft: '0px',
                                            },
                                            '&:last-child': {
                                                paddingRight: '0px',
                                            },
                                        },
                                    }}
                                >
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Name</TableCell>
                                            <TableCell width={100}>Created</TableCell>
                                            <TableCell width={100}>Updated</TableCell>
                                            <TableCell width={60} align="center">
                                                Actions
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {webhooks.map(webhook => (
                                            <SextforceMetricsWebhookTableRow
                                                metricData={metricData}
                                                key={webhook._id}
                                                webhook={webhook}
                                                isUpdatingWebhook={isUpdatingWebhook}
                                                isDeletingWebhook={isDeletingWebhook}
                                                handleUpdateWebhook={handleUpdateWebhook}
                                                handleDeleteWebhook={handleDeleteWebhook}
                                            />
                                        ))}
                                    </TableBody>
                                </Table>
                            </Grid>
                        ) : (
                            <Grid item xs={12}>
                                <Typography component={'span'} variant="body2" sx={{ display: 'block' }}>
                                    No webhooks found.
                                </Typography>
                            </Grid>
                        )}
                    </>
                )}
            </Grid>
        </>
    );
};

export default SextforceMetricsShareDialogWebhook;
